import { ChangeDetectorRef, Directive, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { IReport, ISummaryReport, IUser } from '@shared/interfaces';
import { GuardService } from '../services/guard.service';
import { CacheService } from '../services/cache.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationService } from '../services/navigation.service';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../current-project';
import { ReportGroupByOption } from '../enums/report-group-by-option.enum';
import { SortByOption } from '../enums/sort-by-option.enum';
import { SummaryReportGroupByOption } from '../enums/summary-report-group-by-option.enum';

@Directive()
export abstract class MainViewDirective<T extends IReport | ISummaryReport> implements OnInit {
    protected IS_TRS_PROJECT = IS_TRS_PROJECT;
    protected IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;
    protected type: 'summary-reports' | 'reports';
    protected data: T[] = [];
    protected _items: T[] = [];
    protected currentUser: IUser;
    protected reportingPeriod: string;
    protected noneSortOption = {
        sortBy: null,
        direction: null
    };
    protected groupedItems = {};
    protected selectedGroupByOption: ReportGroupByOption | SummaryReportGroupByOption;

    constructor(
        protected guardService: GuardService,
        protected changeDetection: ChangeDetectorRef,
        protected cache: CacheService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected location: Location,
        protected navigationService: NavigationService
    ) {}

    public async ngOnInit(): Promise<void> {
        this.currentUser = this.guardService.currentUser;
        await this.loadData();
        this.group(this.selectedGroupByOption);
    }

    protected abstract loadData();

    protected abstract group(groupBy: ReportGroupByOption | SummaryReportGroupByOption): void;

    protected abstract get sortByOptions(): { value: SortByOption; direction: 'asc' | 'desc' }[];

    protected get items(): T[] {
        return this._items;
    }

    protected set items(val: T[]) {
        this._items = val;
    }

    protected getCopy(val?: T[]): T[] {
        const items = val ? val : this._items;
        return items ? JSON.parse(JSON.stringify(items)) : null;
    }

    protected getKeys(item: object): string[] {
        return Object.keys(item);
    }

    protected abstract get groupByOption(): typeof ReportGroupByOption | typeof SummaryReportGroupByOption;

    protected get groupByOptions(): ReportGroupByOption[] | SummaryReportGroupByOption[] {
        return Object.values(this.groupByOption);
    }

    protected get reportingPeriods(): string[] {
        const currentYear = new Date().getUTCFullYear();
        if (IS_TRS_PROJECT) {
            return Array.fillFromTo(2022, currentYear - 1)
                .reverse()
                .map(year => `${year}/${year + 1}`);
        } else if (IS_PRODUCT_SAFETY_PROJECT) {
            return Array.fillFromTo(2019, currentYear - 1)
                .reverse()
                .map(year => `${year}/${year + 1}`);
        }
    }

    protected async changeReportingPeriod(reportingPeriod: string): Promise<void> {
        this.reportingPeriod = reportingPeriod === 'all' ? null : reportingPeriod; // not sending the reporting period to the backend in order to get all reports
        await this.loadData();
        this.group(this.selectedGroupByOption);
    }

    protected sortBy(sortOption: { value: SortByOption; direction: string }): void {
        if (!sortOption.value) {
            // If the none option (sortOption values are null) is selected, do not sort
            this.items = this.getCopy(this.data);
            this.group(this.selectedGroupByOption);
            return;
        }

        this.items = this.getCopy(this.items).sort((a, b) => {
            const val = (() => {
                switch (sortOption.value) {
                    case SortByOption.modifiedDate:
                        return new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime();
                    case SortByOption.doc:
                        return (a as IReport).doc - (b as IReport).doc;
                    case SortByOption.status:
                        return (a as IReport).reportStatus.localeCompare((b as IReport).reportStatus);
                    default:
                        return 0;
                }
            })();

            return sortOption.direction === 'desc' ? -val : val;
        });
        this.group(this.selectedGroupByOption);
    }
}
